/**
 * ファイルアップロード・プレビューパネル
 *
 * テスト用画像/PDFのアップロードとプレビュー表示
 */

import {
  Stack,
  Group,
  Text,
  Button,
  Paper,
  Center,
  FileButton,
  Image,
  Loader,
} from "@mantine/core";
import { Sparkles, Image as ImageIcon, FileSearch } from "lucide-react";

interface FileUploaderProps {
  selectedFile: File | null;
  onFileSelect: (file: File | null) => void;
  analyzing: boolean;
  onExecuteAnalysis: () => void;
  previewUrl: string | null;
}

export function FileUploader({
  selectedFile,
  onFileSelect,
  analyzing,
  onExecuteAnalysis,
  previewUrl,
}: FileUploaderProps) {
  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      style={{
        backgroundColor:
          "light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))",
      }}
    >
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <Sparkles size={16} color="var(--mantine-color-blue-6)" />
            <Text size="sm" fw={700}>
              サンプルの伝票から項目を抽出
            </Text>
          </Group>
        </Group>

        <Text size="xs" c="dimmed">
          伝票画像をアップロードして解析すると、AI読取項目を選択式で設定できます。
        </Text>

        <Group grow>
          <FileButton
            onChange={onFileSelect}
            accept="image/png,image/jpeg,application/pdf"
          >
            {(props) => (
              <Button
                {...props}
                variant="default"
                size="xs"
                leftSection={<ImageIcon size={14} />}
              >
                {selectedFile ? "画像を変更" : "画像を選択"}
              </Button>
            )}
          </FileButton>
          {selectedFile && (
            <Button
              onClick={onExecuteAnalysis}
              loading={analyzing}
              size="xs"
              leftSection={<FileSearch size={14} />}
            >
              解析実行
            </Button>
          )}
        </Group>

        {(previewUrl || analyzing) && (
          <Paper
            withBorder
            p={4}
            radius="sm"
            bg="light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))"
          >
            <Center
              bg="light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))"
              style={{
                borderRadius: "4px",
                overflow: "hidden",
                position: "relative",
                minHeight: "150px",
              }}
            >
              {previewUrl ? (
                selectedFile?.type === "application/pdf" ? (
                  <object
                    data={`${previewUrl}#toolbar=0&navpanes=0&view=FitH`}
                    type="application/pdf"
                    width="100%"
                    height="300px"
                    style={{ display: "block" }}
                  >
                    <p>PDFを表示できません。</p>
                  </object>
                ) : (
                  <Image
                    src={previewUrl}
                    fit="contain"
                    h={200}
                    style={{ opacity: analyzing ? 0.5 : 1 }}
                  />
                )
              ) : (
                <Text size="xs" c="dimmed">
                  プレビュー
                </Text>
              )}
              {analyzing && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255,255,255,0.5)",
                  }}
                >
                  <Loader size="sm" color="blue" />
                </div>
              )}
            </Center>
          </Paper>
        )}
      </Stack>
    </Paper>
  );
}
